import { CheckIcon, InfoIcon } from '@blueprinthq/joy'
import { Button, Flex, HStack, Stack, Text } from '@chakra-ui/react'
import { Tag } from '@components'
import { useExperienceManager } from '@hooks'
import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import type { PlanOptionDtoPromotion } from '~/clinician-api/models'
import { PlusFreeTrialStatus } from './plus-free-trial-status'

interface PlanCardProps {
  title: string
  price: number
  features: string[]
  buttonText: string
  buttonAction: () => void
  isCurrentPlan?: boolean
  showCurrentPlanTag?: boolean
  showCurrentTrialTag?: boolean
  priceEmbelishment?: string
  showPlusPlanFreeTrial?: boolean
  buttonVariant?: string
  isDisabled?: boolean
  promotion?: PlanOptionDtoPromotion
}

const PlanCard: React.FC<PlanCardProps> = ({
  title,
  price,
  priceEmbelishment,
  features,
  buttonText,
  buttonVariant = 'solid',
  buttonAction,
  showPlusPlanFreeTrial,
  isCurrentPlan,
  showCurrentPlanTag,
  showCurrentTrialTag,
  isDisabled,
  promotion
}) => {
  const { isDocumentationAutomationFreePlan } = useExperienceManager()

  const { percentageOff, duration, discountedAmountCents } = promotion || {}

  return (
    <Flex
      border="1px solid"
      flexDirection="column"
      borderRadius="8px"
      borderColor="pale_gray"
      gap="24px"
      p={6}
      flex="1"
      minH="461px"
      opacity={isDisabled ? 0.6 : 1}
      cursor={isDisabled ? 'not-allowed' : 'auto'}
    >
      <Stack flex="1" spacing="0" gap="16px">
        <Flex gap="8px" alignItems="center">
          <Text fontWeight="bold">{title}</Text>
          {showCurrentPlanTag ? (
            <Tag>Your Current Plan</Tag>
          ) : showCurrentTrialTag ? (
            <Tag>Current Trial</Tag>
          ) : null}
        </Flex>
        <Stack spacing="0">
          <Flex align="center" gap={2}>
            <Text color="dark_gray">{priceEmbelishment}</Text>
            {isCurrentPlan && !isDocumentationAutomationFreePlan && (
              <Text
                as={RouterLink}
                to="/settings/update-your-plan"
                color="primary"
                textDecoration="none"
                _hover={{ textDecoration: 'underline' }}
              >
                Change
              </Text>
            )}
          </Flex>
          <HStack alignItems="flex-start" spacing="0">
            {discountedAmountCents && price !== -1 && (
              <Text
                color="#a1a1a1"
                fontWeight="bold"
                fontSize="24px"
                textDecoration="line-through"
                mr={1}
              >
                ${price / 100}
              </Text>
            )}
            <Text color="primary" fontWeight="bold" fontSize="24px">
              {price === -1
                ? 'Custom pricing'
                : discountedAmountCents
                  ? `$${(discountedAmountCents / 100).toFixed(2)}/month`
                  : `$${price / 100}/month`}
            </Text>
          </HStack>
        </Stack>
        <Stack spacing="0" gap="16px">
          {percentageOff && duration && (
            <Text
              fontSize="12px"
              color="white"
              px={2}
              py={1}
              bg="#0d8443"
              borderRadius="3px"
              textAlign="center"
              width="fit-content"
              display="inline-block"
            >
              {percentageOff}% off for {duration} months
            </Text>
          )}
          <Text fontSize="14px">
            <Text as="b" fontSize="inherit">
              {title}
            </Text>{' '}
            plan includes:
          </Text>
          <Stack spacing="0" gap="8px">
            {features.map((feature, index) => (
              <Flex
                key={index}
                alignItems="center"
                justifyContent="space-between"
              >
                <Flex gap="8px">
                  <InfoIcon fill="medium_gray" size="sm" />
                  <Text fontSize="14px" lineHeight="16px">
                    {feature}
                  </Text>
                </Flex>
                {(isCurrentPlan || showCurrentTrialTag) && (
                  <CheckIcon fill="primary" size="sm" />
                )}
              </Flex>
            ))}
          </Stack>
        </Stack>
        {showPlusPlanFreeTrial && <PlusFreeTrialStatus />}
      </Stack>
      <Button
        m="0"
        size="md"
        isFullWidth
        onClick={!isDisabled ? buttonAction : undefined}
        variant={buttonVariant}
        borderColor="pale_gray"
        isDisabled={isDisabled}
      >
        {isDisabled ? 'Coming Soon' : buttonText}
      </Button>
    </Flex>
  )
}

export default PlanCard
